import * as React from "react";
import { Tree, TreeNodeDatum } from "react-d3-tree";
import * as uuid from "uuid";

import styled from "styled-components";
import {
  DogApi,
  DogGridDto,
  DogReferentialItem,
  EBroodRequestDogType,
  EDogSex,
  EPlatform,
  ErrorDetails,
  PaginatedSearch,
  ReferentialApi,
  SearchDogReferentialQuery,
} from "../../../../api";
import { useApiService, useTl, useWindow } from "../../../../hooks";
import {
  FieldSet,
  IDataTableColumn,
  Loading,
  showError,
  useApiEffect,
  useGridState,
  useSearchApi,
} from "nsitools-react";
import { useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { Classes, Colors, Dialog, Tooltip } from "@blueprintjs/core";
import { ETLCodes } from "../../../../locales";
import { AddButton, CustomDataTable } from "../../../../components";
import { SyntheticEvent, useCallback } from "react";
import { HierarchyPointNode } from "d3-hierarchy";
import { renderElement } from "../../../../components/genealogyD3TreeUtils.tsx";

export interface IDogGenealogyPageProps {
  dogId: number;
  platform?: EPlatform;
}

const height = 780;

const Container = styled.div`
  border: solid 1px rgba(16, 22, 26, 0.15);

  /* TODO: change this to 100% when pagebase is fixed */
  height: ${height}px;
`;

const DataTableContainer = styled.div`
  tbody tr:hover {
    /* font-weight: bold */
    text-shadow: 0px 0px 1px black !important;
  }
`;

export const DogGenealogyPage: React.FunctionComponent<IDogGenealogyPageProps> = ({
  dogId,
  platform = EPlatform.CARE4DOGS,
}) => {
  const { t, tUnsafe } = useTl();
  const { currentLanguage } = useGlobalData();
  const { openNewTab } = useWindow();
  const api = useApiService(DogApi);
  const refApi = useApiService(ReferentialApi);

  const [selectDogLoading, setselectDogLoading] = React.useState(false);
  const [selectedNode, setSelectedNode] = React.useState<any>({
    dogId: +dogId,
  });
  const [dialogOpened, setDialogOpened] = React.useState(false);
  const [dialogNeverOpened, setDialogNeverOpened] = React.useState(true);
  const [generation, setgeneration] = React.useState<number>(2);
  const [dogData, dogDataLoading, , refreshDogData] = useApiEffect(
    () => api.genealogy(+dogId, generation, currentLanguage.languageId),
    [dogId, currentLanguage, generation]
  );

  /**
   * _transformToHierarchy - Transforms a flat array of parent-child links
   * into a hierarchy.
   * @private
   * @param {array<object>} links           Set of parent-child link objects
   * @param {array<string>|undefined} attributeFields Set of `link` fields to be used as attributes
   *
   * @return {array<object>} Single-element array containing the root node object.
   */
  const _transformToHierarchy = React.useCallback(
    (links, attributeFields) => {
      const nodesByName = {};

      const assignNode = (id) => {
        if (!nodesByName[id]) {
          nodesByName[id] = { id };
        }
        return nodesByName[id];
      };

      const assignNodeWithAttributes = (id, name, attributes) => {
        if (!nodesByName[id]) {
          nodesByName[id] = {
            name,
            attributes,
          };
          nodesByName[id].id = id;
        }
        return nodesByName[id];
      };

      // Create nodes for each unique source and target.
      links.forEach((link) => {
        // if `attributeFields` is defined, create/overwrite current `link.attributes`
        if (attributeFields) {
          const customAttributes = {};
          attributeFields.forEach((field) => {
            let f = link[field];
            if (f == null) {
            } else {
              if (f instanceof Date) {
                f = f.toLocaleDateString();
              }
              let attrName = field;
              if (field === "breedName") {
                attrName = t(ETLCodes.BreedName);
              } else if (field === "pedigree") {
                attrName = t(ETLCodes.Pedigree);
              } else if (field === "birthDate") {
                attrName = t(ETLCodes.BirthDate);
              } else if (field === "deathDate") {
                attrName = t(ETLCodes.DeathDate);
              }
              customAttributes[attrName] = "" + f;
            }
          });
          link.attributes = customAttributes;
        }

        if (link.name === "*undefined_Father") {
          link.name = t(ETLCodes.UnknownFather);
        } else if (link.name === "*undefined_Mother") {
          link.name = t(ETLCodes.UnknownMother);
        }

        link.source = link.parent ? assignNode(link.parent) : null;
        link.target = assignNodeWithAttributes(link.child, link.name, link.attributes);

        if (link.sex === EDogSex.Female) {
          link.target.renderCustomNodeElement = (nodeProps) => {
            return renderElement(
              "rect",
              {
                width: 240,
                height: 67,
                y: -18,
                x: 3,
                fill: "#FFD6E8",
                stroke: "#BF96A8",
                strokeWidth: 2,
              },
              nodeProps
            );
          };
        } else if (link.sex === EDogSex.Male) {
          link.target.renderCustomNodeElement = (nodeProps) => {
            return renderElement(
              "rect",
              {
                width: 240,
                height: 67,
                y: -18,
                x: 3,
                fill: "#D0E2FF",
                stroke: "#90A2BF",
                strokeWidth: 2,
              },
              nodeProps
            );
          };
        } else if (link.dogType === EBroodRequestDogType.Mother) {
          link.target.renderCustomNodeElement = (nodeProps) => {
            return renderElement(
              "rect",
              {
                width: 180,
                height: 18,
                y: -18,
                x: 0,
                fill: "#EEEEEE",
                stroke: "#BF96A8",
                strokeWidth: 2,
              },
              nodeProps
            );
          };
        } else if (link.dogType === EBroodRequestDogType.Father) {
          link.target.renderCustomNodeElement = (nodeProps) => {
            return renderElement(
              "rect",
              {
                width: 180,
                height: 18,
                y: -18,
                x: 0,
                fill: "#EEEEEE",
                stroke: "#90A2BF",
                strokeWidth: 2,
              },
              nodeProps
            );
          };
        }

        const parent = link.source;
        const child = link.target;

        child.dog = link;
        // child.parent = parent.name || null;
        if (parent) {
          // parent.id = link.parentNodeId;
          parent.collapsed = child.collapsed = false; // eslint-disable-line
          // NOTE We assign to a custom `_children` field instead of D3's reserved
          // `children` to avoid update anomalies when collapsing/re-expanding nodes.
          parent.children ? parent.children.push(child) : (parent.children = [child]);
        }
      });

      // Extract & return the root node
      //console.log("LINK:", links);
      const rootLinks = links.filter((link) => !link.source);
      const rootNode = [rootLinks[0].target];
      return rootNode;
    },
    [t]
  );

  const treeData = React.useMemo(() => {
    if (dogData) {
      const attributeFields = ["breedName", "pedigree", "birthDate", "deathDate"];
      return _transformToHierarchy(dogData, attributeFields);
    } else {
      const empty = [];
      return empty;
    }
  }, [_transformToHierarchy, dogData]);

  const [updateError, setCurrentError] = React.useState<ErrorDetails>();

  const manageError = React.useCallback(
    (error: any) => {
      // console.log("ERROR");
      // console.log(typeof error);
      // console.log(error);
      // console.log(error.name);
      // console.log(error.message);

      const details: ErrorDetails = JSON.parse(error.response);
      if (details?.errorCode) {
        setCurrentError(details);
      } else {
        showError(t(ETLCodes.Error));
      }
    },
    [t]
  );
  React.useEffect(() => {
    if (updateError && updateError.errorCode) {
      showError(tUnsafe(`BackendError_${updateError.errorCode}`));
    }
  }, [updateError, tUnsafe]);

  const onLinkClickFunction = React.useCallback((linkSource, linkTarget, evt) => {
    // console.log(evt);
    // console.log(linkSource);
    // console.log(linkTarget);
  }, []);

  const createNewDog = React.useCallback(() => {
    setDialogOpened(false);
    openNewTab(`${ERoutes.dog}/0/detail/edit`);
  }, [openNewTab]);

  const closeDialogFunction = React.useCallback(() => {
    setDialogOpened(false);
  }, []);

  function drawStepPath(linkData, orientation) {
    const { source, target } = linkData;
    const deltaY = target.y - source.y - 32; // -32 to starts further left from the right edge

    let draw = `M${source.y},${source.x} H${source.y + deltaY} V${target.x} H${target.y}`;
    return draw;
  }

  const getDog = React.useCallback(
    async (selecteNode: any) => {
      const currentDog = await api.getDog(selectedNode.parentNodeId ? selectedNode.parentNodeId : selectedNode.dogId);
      return currentDog;
    },
    [api, selectedNode.dogId, selectedNode.parentNodeId]
  );

  // ========================================= DOG SEARCH =========================================
  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [15, 25, 50],
    pageSize: 15,
  });

  const searchFunction = React.useCallback(
    (sObj?: PaginatedSearch) => {
      return refApi.searchDogs(
        SearchDogReferentialQuery.fromJS({
          sex: selectedNode.dogType === EBroodRequestDogType.Father ? EDogSex.Male : EDogSex.Female,
          dogId: +dogId,
          filterBreeds: true,
          sObj,
        })
      );
    },
    [dogId, refApi, selectedNode.dogType]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const onDogSelected = React.useCallback(
    async (selectedNodeInfo, selectedDog) => {
      setselectDogLoading(true);
      const childDog = await getDog(selectedNodeInfo);
      //console.log("SELECTED DOG", childDog, selectedNodeInfo, selectedDog);
      api
        .updateDogRelative(childDog.dogId, selectedNodeInfo.dogType, selectedDog.dogId)
        .then(() => {
          setDialogOpened(false);
          refreshDogData();
        })
        .catch((error) => manageError(error));
      setselectDogLoading(false);
    },
    [api, getDog, manageError, refreshDogData]
  );

  const onClickFunction = React.useCallback(
    (source: HierarchyPointNode<TreeNodeDatum>, event: SyntheticEvent) => {
      const dog = (source.data as any).dog;
      if (dog != null) {
        setSelectedNode(dog);
        if (dog.dogId > 0) {
          // history.push(`${ERoutes.dog}/${source.dogId}/detail/view`);
          openNewTab(`${ERoutes.dog}/${dog.dogId}/detail/view`);
        } else {
          setDialogOpened(true);
          search();
          if (dialogNeverOpened) {
            setDialogNeverOpened(false);
          }
        }
      }
    },
    [dialogNeverOpened, openNewTab, search]
  );

  // ================================== DOG SEARCH TABLE ============================================
  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => t(ETLCodes.Name),
        fieldName: "displayValue",
      },
      {
        header: () => t(ETLCodes.Sex),
        fieldName: "sex",
        render: (row: DogGridDto) => <div>{tUnsafe(row.sex)}</div>,
      },
      {
        fieldName: "pedigrees",
        computed: true,
        disableSorting: true,
        header: () => t(ETLCodes.Pedigree),
        render: (row: DogReferentialItem) => {
          if (row.pedigrees?.length > 0) {
            return (
              <span style={{ textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {row.pedigree}
                {row.pedigrees.length > 1 && (
                  <span>
                    &nbsp;
                    <Tooltip
                      content={
                        <div>
                          {row.pedigrees.map((pedigree, i) => (
                            <div key={i}>{pedigree}</div>
                          ))}
                        </div>
                      }
                      position="right"
                    >
                      <div style={{ cursor: "pointer" }}>...</div>
                    </Tooltip>
                  </span>
                )}
              </span>
            );
          } else {
            return <div />;
          }
        },
      },
      {
        header: () => t(ETLCodes.Identification1),
        fieldName: "dogChip",
      },
      {
        header: () => t(ETLCodes.Identification2),
        fieldName: "secondDogChip",
      },
    ],
    [t, tUnsafe]
  );

  return (
    <Loading isLoading={dogDataLoading}>
      {!dogDataLoading && (
        <Container>
          <AddButton text={t(ETLCodes.LoadMore)} onClick={() => setgeneration(generation + 1)} />
          {
            <Tree
              data={treeData}
              orientation="horizontal"
              renderCustomNodeElement={(nodeProps) => {
                if ((nodeProps.nodeDatum as any).renderCustomNodeElement)
                  return (nodeProps.nodeDatum as any).renderCustomNodeElement(nodeProps);
                return renderElement(
                  "rect",
                  {
                    width: 240,
                    height: 20,
                    y: -20,
                    x: 0,
                    fill: "lightgray",
                    stroke: "gray",
                    strokeWidth: 2,
                  },
                  nodeProps
                );
              }}
              translate={{ x: 8, y: height / 2 }}
              nodeSize={{
                x: 288,
                y: 136,
              }} // box is 240 by 67, 288 to make some gap between boxes along X axis, 140 is two times the height plus extra gap
              separation={{ siblings: 1, nonSiblings: 1 }}
              collapsible={false} // clicking on a defined node opens detail's dog page, cliking on an undefined node opens dialog
              onNodeClick={onClickFunction}
              onLinkClick={onLinkClickFunction} // not used
              pathFunc={drawStepPath}
              zoom={0.7} // adapt first zoom level used for display, 0.7 to allow displaying 4 levels (to validate on target dispplay size)
              transitionDuration={0}
            />
          }
          {!dialogNeverOpened && (
            <Dialog
              title={t(
                selectedNode.dogType === EBroodRequestDogType.Father
                  ? ETLCodes.DogsSelectFather
                  : ETLCodes.DogsSelectMother
              )}
              isOpen={dialogOpened}
              onClose={closeDialogFunction}
              canEscapeKeyClose={true}
              canOutsideClickClose={true}
              isCloseButtonShown={true}
              style={{
                width: "88%",
                height: "98%",
                minHeight: "500px",
                background: Colors.WHITE,
              }}
              className={Classes.DIALOG}
            >
              <div className={Classes.DIALOG_BODY}>
                <FieldSet
                  title={t(ETLCodes.Dogs)}
                  rightElement={<AddButton onClick={createNewDog} text={t(ETLCodes.General_Add)} />}
                >
                  <DataTableContainer>
                    <CustomDataTable
                      tableState={tableState}
                      loading={loading || selectDogLoading}
                      columns={columns}
                      onRowClick={(value) =>
                        platform === EPlatform.CARE4DOGS ? onDogSelected(selectedNode, value) : {}
                      }
                    />
                  </DataTableContainer>
                </FieldSet>
              </div>
            </Dialog>
          )}
        </Container>
      )}
    </Loading>
  );
};
